import { Typography, useTheme, useMediaQuery, Box, BoxProps, Theme } from '@mui/material';
import {
  MRT_RowData,
  MRT_SortingState,
  MRT_TableOptions,
  MRT_TableContainer as Table,
  useMaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableInstance,
  MRT_Cell,
} from 'material-react-table';
import { Fragment, ReactNode, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import MuiPaginationCustom, { MuiPaginationCustomProps } from './MuiPaginationCustom';
import { SHELL } from 'config/setupTheme';

const MIN_COLUMN_WIDTH = 112;

/** Cột đều nhau, chỉ kẻ dọc giữa các cột. */
const evenColumnSx = (theme: Theme) => ({
  '& .MuiTable-root, & .MuiTableHead-root, & .MuiTableBody-root': {
    minWidth: 'max-content',
  },
  '& .MuiTableRow-head, & .MuiTableBody-root .MuiTableRow-root': {
    display: 'flex',
    width: 'max-content',
    minWidth: '100%',
  },
  // Màn hẹp: cột không co dưới mức này, bảng cuộn ngang thay vì ngắt chữ từng ký tự.
  '& .MuiTableCell-head, & .MuiTableCell-body': {
    flex: '1 1 0px !important',
    width: '0 !important',
    minWidth: `${MIN_COLUMN_WIDTH}px !important`,
    maxWidth: 'none !important',
    borderLeft: 'none',
    borderRight: 'none',
  },
  '& .MuiTableCell-head:not(:last-of-type), & .MuiTableCell-body:not(:last-of-type)': {
    borderRight: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(244, 246, 252, 0.12)' : SHELL.line}`,
  },
  '& .MuiTableCell-root[data-pinned="true"]': {
    opacity: '1 !important',
    borderLeft: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(244, 246, 252, 0.12)' : SHELL.line}`,
  },
  '& .MuiTableCell-head': {
    alignItems: 'center',
    minHeight: 72,
    height: 'auto',
    overflow: 'visible',
    paddingTop: '14px !important',
    paddingBottom: '14px !important',
  },
  '& .Mui-TableHeadCell-Content, & .Mui-TableHeadCell-Content-Labels, & .Mui-TableHeadCell-Content-Wrapper': {
    overflow: 'visible',
    height: 'auto',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.45,
  },
});

interface Props<T extends MRT_RowData = MRT_RowData> {
  paginationProps?: MuiPaginationCustomProps;
  onSortingChange?: (value: MRT_SortingState) => void;
  renderRowActions?: (props: {
    isTablet: boolean;
    closeMenu?: () => void;
    row: MRT_Row<T>;
    cell?: MRT_Cell<T>;
    staticRowIndex?: number;
    table: MRT_TableInstance<T>;
  }) => ReactNode[];
  boxProps?: BoxProps;
}
const MuiTable = <T extends MRT_RowData = MRT_RowData>(
  props: Props<T> & Omit<MRT_TableOptions<T>, 'onSortingChange' | 'renderRowActions'>
) => {
  const {
    boxProps,
    paginationProps,
    state,
    muiSearchTextFieldProps,
    muiTablePaperProps,
    muiTopToolbarProps,
    onSortingChange,
    muiTableBodyCellProps,
    muiTableHeadCellProps,
    enableRowVirtualization,
    renderBottomToolbarCustomActions,
    columns,
    renderRowActions,
    ...rest
  } = props;
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('laptop'));
  const headerBg = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : theme.palette.grey[100];
  const [sorting, setSort] = useState<MRT_SortingState>(state?.sorting || []);
  const mappedColumns = useMemo(() => {
    return [...columns].map((column) => ({
      ...column,
      size: 160,
      minSize: 30,
      grow: true,
      enableSorting: column.enableSorting ?? false,
    })) as MRT_ColumnDef<T, any>[];
  }, [columns]);

  useEffect(() => {
    if (state?.sorting) setSort(state?.sorting);
  }, [state?.sorting]);

  const table = useMaterialReactTable<T>({
    renderRowActions: isTablet
      ? undefined
      : (params) =>
          (renderRowActions &&
            renderRowActions({ isTablet, ...params }).map((value, index) => (
              <Fragment key={index}>{value}</Fragment>
            ))) ||
          [],
    renderRowActionMenuItems: (params) => {
      return isTablet
        ? renderRowActions?.({ isTablet, ...params }).map((value, index) => (
            <Box key={index}>{value}</Box>
          )) || []
        : [];
    },
    getRowId: (originalRow) => originalRow.id,
    columns: mappedColumns,
    layoutMode: 'grid',
    enableColumnPinning: true,
    initialState: {
      columnPinning: {
        right: ['mrt-row-actions'],
      },
    },
    state: {
      density: 'compact',
      sorting,
      isLoading: false,
      ...state,
    },
    renderEmptyRowsFallback: () => (
      <Typography
        variant="body1"
        color="textSecondary"
        sx={(theme) => ({
          margin: 0,
          fontStyle: 'italic',
          height: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          [theme.breakpoints.up('tablet')]: {
            height: 240,
          },
        })}
        component={'div'}
      >
        <FormattedMessage id="emptyData" />
      </Typography>
    ),
    muiDetailPanelProps: () => ({
      sx: () => ({
        p: 0,
      }),
    }),
    muiTableProps: {
      sx: {
        '& .Mui-TableBodyCell-DetailPanel': {
          border: 'none',
        },
        '& .MuiTableRow-footer': {
          bgcolor: 'grey.50',
        },
        '& .MuiTableCell-root[data-pinned="true"]:before': {
          backgroundColor: 'background.paper',
        },
        '& .MuiTableCell-head[data-pinned="true"]:before': {
          backgroundColor: headerBg,
        },
      },
    },
    muiTableHeadCellProps: () => ({
      align: 'center',
      sx: {
        bgcolor: headerBg,
        whiteSpace: 'pre-wrap',
        py: 1.3,
        '&:before': { backgroundColor: headerBg },
        '&[data-pinned="true"]': { backgroundColor: headerBg },
        '& .Mui-TableHeadCell-Content-Wrapper': {
          whiteSpace: 'pre-wrap',
          lineHeight: 1.45,
          overflow: 'visible',
        },
      },
      size: 'small',
    }),
    muiTableBodyCellProps: {
      align: 'center',
      sx: {
        minWidth: 'unset',
        whiteSpace: 'pre-wrap',
        lineHeight: 1.45,
        wordBreak: 'break-word',
      },
      size: 'small',
    },
    displayColumnDefOptions: {
      'mrt-row-expand': {
        size: 10,
        muiTableHeadCellProps: {
          sx: {
            maxWidth: 40,
          },
        },
        muiTableBodyCellProps: {
          sx: {
            maxWidth: 40,
          },
        },
      },
      'mrt-row-actions': {
        header: 'action',
        Header: isTablet ? <Box /> : <FormattedMessage id="action" />,
        size: 160,
        minSize: 30,
        grow: true,
      },
    },
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnActions: false,
    enablePagination: false,
    enableTableFooter: false,
    enableGlobalFilter: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    // enableSorting: false,
    manualSorting: true,
    isMultiSortEvent: () => true,
    enableRowNumbers: false,
    positionActionsColumn: 'last',
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.paper,
      draggingBorderColor: theme.palette.secondary.main,
    }),
    onSortingChange: (sortingUpdater: any) => {
      const newSortVal = sortingUpdater(sorting);
      setSort(newSortVal);
      if (onSortingChange) onSortingChange(newSortVal);
    },
    ...rest,
  });

  return (
    <Box
      className={'flex flex-col overflow-hidden flex-1'}
      {...boxProps}
      sx={[evenColumnSx, boxProps?.sx] as BoxProps['sx']}
    >
      <Table table={table} />
      <>{paginationProps && <MuiPaginationCustom {...paginationProps} />}</>
    </Box>
  );
};
export default MuiTable;
